import { LegalEntityCountry } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useState } from 'react';
import { AccountView, BeneficiaryView } from '../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { CreateBeneficiaryFlow } from '../../create-beneficiary-flow/create-beneficiary-flow';
import { PaymentDetails } from '../../payment-flow-storage';
import { usePaymentFlow } from '../../use-payment-flow';
import { InvoiceDetailsWithOptionalCurrency } from './invoice-payment-information-without-toggle';
import { KnownBeneficiaryFlow } from './known-beneficiary-flow';
import { UnsupportedInvoiceDetails } from './unsupported-invoice-details';

type Steps = 'BENEFICIARY_DETAILS' | 'PAYMENT_DETAILS';

type UnknownBeneficiaryCreateNewFlowProps = {
  isLoading: boolean;
  companyCountry: LegalEntityCountry;
  accounts: AccountView[];
  beneficiaries: BeneficiaryView[];
  invoiceDetails: InvoiceDetailsWithOptionalCurrency;
  onCreateBeneficiary: (
    formValues: Parameters<ComponentProps<typeof CreateBeneficiaryFlow>['onCreateBeneficiary']>[0],
  ) => Promise<BeneficiaryView | undefined>;
  onMakePayment: (paymentDetails: PaymentDetails) => Promise<void>;
  onCancel: () => void;
};

export const UnknownBeneficiaryCreateNewFlow = ({
  isLoading,
  companyCountry,
  accounts,
  beneficiaries,
  invoiceDetails,
  onCreateBeneficiary,
  onMakePayment,
  onCancel,
}: UnknownBeneficiaryCreateNewFlowProps) => {
  const [step, setStep] = useState<Steps>('BENEFICIARY_DETAILS');
  const { updateSelectedBeneficiaryId, getSelectedBeneficiaryId } = usePaymentFlow();

  switch (step) {
    case 'BENEFICIARY_DETAILS':
      return (
        <CreateBeneficiaryFlow
          companyCountry={companyCountry}
          isWarningCardVisible={true}
          isLoading={isLoading}
          onCreateBeneficiary={async (formValues) => {
            const beneficiary = await onCreateBeneficiary(formValues);

            if (!beneficiary) {
              // No Sentry alert as we already do it in the onCreateBeneficiary function

              return;
            }

            updateSelectedBeneficiaryId(beneficiary.id);

            setStep('PAYMENT_DETAILS');
          }}
          onGoBack={onCancel}
        />
      );
    case 'PAYMENT_DETAILS': {
      const selectedBeneficiaryId = getSelectedBeneficiaryId();

      const selectedBeneficiary = beneficiaries.find((beneficiary) => beneficiary.id === selectedBeneficiaryId);

      if (!selectedBeneficiary) {
        Sentry.captureMessage(
          `The selected beneficiary id can't be found among the beneficiaries in the PAYMENT_DETAILS step in UNKNOWN_BENEFICIARY flow`,
          {
            extra: {
              selectedBeneficiaryId,
              beneficiaryIds: beneficiaries.map((b) => b.id),
              step,
              message: `The selected beneficiary id can't be found among the beneficiaries in the PAYMENT_DETAILS step in UNKNOWN_BENEFICIARY flow`,
            },
          },
        );

        return (
          <UnsupportedInvoiceDetails
            title="Oups, something went wrong"
            variantConfig={{ variant: 'GENERIC_ERROR' }}
            onGoBack={onCancel}
          />
        );
      }

      const beneficiaryCurrency = selectedBeneficiary.destination.currency;
      const beneficiariesWithMatchingCurrency = beneficiaries.filter(
        (beneficiary) => beneficiary.destination.currency === beneficiaryCurrency,
      );

      return (
        <KnownBeneficiaryFlow
          accounts={accounts}
          beneficiaries={beneficiariesWithMatchingCurrency}
          // Replace the null suggestedBeneficiaryId with the one that was created in the BENEFICIARY_DETAILS step
          initialInvoiceDetails={{
            ...invoiceDetails,
            currency: beneficiaryCurrency,
            suggestedBeneficiaryId: selectedBeneficiaryId,
          }}
          isLoading={isLoading}
          onMakePayment={onMakePayment}
          onCancelTransfer={() => setStep('BENEFICIARY_DETAILS')}
        />
      );
    }
    default:
      return exhaustiveCheck(step);
  }
};
